import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {Page} from '@PosterWhiteboard/page/page.class';

export interface ClusterBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * The current selection partitioned into rigid units: every saved group (shared `groupId`)
 * forms one cluster, every loose item its own. Group-aware selection commands (align,
 * distribute) move a cluster by one shared delta so a saved group's internal layout survives.
 */
export const getSelectionClusters = (page: Page): ItemType[][] => {
  const clustersById = new Map<string, ItemType[]>();
  for (const item of page.getSelectedItems()) {
    const clusterId = item.groupId ?? item.uid;
    const cluster = clustersById.get(clusterId);
    if (cluster) {
      cluster.push(item);
    } else {
      clustersById.set(clusterId, [item]);
    }
  }
  return [...clustersById.values()];
};

/**
 * Bounds in the selection's parent-relative space — the space child `left`/`top` and the
 * `±group.width/2` alignment targets live in. `getCenterPoint()` would give scene coordinates
 * for a grouped object and shift every cluster toward the page origin.
 */
export const getClusterBounds = (cluster: ItemType[]): ClusterBounds => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const item of cluster) {
    const corners = item.fabricObject.getCornerPoints(item.fabricObject.getRelativeCenterPoint());
    for (const corner of [corners.tl, corners.tr, corners.bl, corners.br]) {
      minX = Math.min(minX, corner.x);
      maxX = Math.max(maxX, corner.x);
      minY = Math.min(minY, corner.y);
      maxY = Math.max(maxY, corner.y);
    }
  }
  return {minX, maxX, minY, maxY};
};

export const moveCluster = (cluster: ItemType[], deltaX: number, deltaY: number): void => {
  for (const item of cluster) {
    item.fabricObject.set({
      left: item.fabricObject.left + deltaX,
      top: item.fabricObject.top + deltaY,
    });
    item.fabricObject.setCoords();
  }
};
